import { useEffect, useRef, useState } from 'react';
import apiClient from '../services/webTicketApiClient';

const receiptKey = (taskName, commentId, role) => JSON.stringify([taskName, commentId, role]);

export default function useTicketReadReceipts(ticket, role, enabled = true) {
  const commentsRef = useRef(null);
  const [receipts, setReceipts] = useState([]);
  const [readError, setReadError] = useState('');
  const jobRunId = ticket?.jobRunId;

  useEffect(() => {
    setReceipts([]);
    setReadError('');
    if (!enabled || !jobRunId || !commentsRef.current) return undefined;
    if (!window.IntersectionObserver) {
      setReadError('Read receipts are unavailable in this browser.');
      return undefined;
    }

    let active = true;
    let busy = false;
    let ready = role !== 'Admin';
    const visible = new Map();
    const confirmed = new Map();
    const base = `/api/v2/web/${role === 'Admin' ? 'admin/' : ''}tickets/${encodeURIComponent(jobRunId)}`;

    const sync = async () => {
      if (!active || !ready || busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const response = await apiClient.get(`${base}/read-receipts`);
        if (!active) return;
        if (!Array.isArray(response.data)) throw new Error('Invalid read receipt response.');
        response.data.forEach(receipt => {
          confirmed.set(receiptKey(receipt.taskName, receipt.commentId, receipt.readerRole), receipt);
        });
        for (const [key, comment] of visible) {
          if (!active || document.visibilityState !== 'visible') break;
          if (confirmed.has(key)) continue;
          const result = await apiClient.post(
            `${base}/tasks/${encodeURIComponent(comment.taskName)}/comments/${encodeURIComponent(comment.id)}/seen`
          );
          if (!active) return;
          if (!result.data?.seenAt) throw new Error('Read receipt was not confirmed by the server.');
          confirmed.set(key, result.data);
        }
        if (active) {
          setReceipts([...confirmed.values()]);
          setReadError('');
        }
      } catch (error) {
        if (active) {
          console.error('Failed to synchronize ticket read receipts:', error);
          setReadError(error.response?.status === 401
            ? 'Sign in again to enable read receipts.'
            : 'Unable to update read receipts. Retrying automatically.');
        }
      } finally {
        busy = false;
      }
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const { taskName, commentId, author } = entry.target.dataset;
        const key = receiptKey(taskName, commentId, role);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.1 && author?.toLowerCase() !== role.toLowerCase()) {
          visible.set(key, { taskName, id: commentId });
        } else {
          visible.delete(key);
        }
      });
      sync();
    }, { threshold: [0, 0.1] });

    commentsRef.current.querySelectorAll('[data-comment-id]').forEach(element => {
      if (element.dataset.commentId) observer.observe(element);
    });

    const start = async () => {
      if (role === 'Admin') {
        try {
          const response = await apiClient.get('/api/v2/web/auth/session', {
            headers: { 'X-Trust-Session-Refresh': '1' },
          });
          if (!active) return;
          sessionStorage.setItem('trust_web_csrf', response.data.csrfToken);
          ready = true;
        } catch (error) {
          if (active) {
            console.error('Failed to initialize admin read receipts:', error);
            setReadError('Sign in again to enable read receipts.');
          }
          return;
        }
      }
      sync();
    };
    start();
    const interval = setInterval(sync, 5000);
    document.addEventListener('visibilitychange', sync);
    return () => {
      active = false;
      observer.disconnect();
      clearInterval(interval);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [jobRunId, ticket, role, enabled]);

  const getSeenLabel = (task, comment) => {
    if (!comment.id || !task.comments?.length) return '';
    const timestamp = value => {
      const parsed = new Date(value?.$date || value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const latest = task.comments.reduce((last, current) => (
      timestamp(current.timestamp) > timestamp(last.timestamp) ? current : last
    ));
    if (latest.id !== comment.id) return '';
    const otherRole = comment.userId?.toLowerCase() === 'admin' ? 'Customer' : 'Admin';
    const seen = receipts.some(receipt => receipt.jobRunId === jobRunId
      && receipt.taskName === task.taskName && receipt.commentId === comment.id
      && receipt.readerRole === otherRole);
    return seen ? `Seen by ${otherRole === 'Admin' ? 'admin' : 'customer'}` : '';
  };

  return { commentsRef, readError, getSeenLabel };
}
